import React from 'react'
import { css } from '@emotion/css'
import { CodeSnippet } from '../../legacy-design-system/code-snippet'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { UserInput, HttpRequest } from './shared'
import { WithRedactedTooltip } from './with-redacted-tooltip'

const taintedCss = css`
  color: ${theme.color.red50};
`

interface AttackEventRequestProps {
  readonly request: HttpRequest
  readonly userInput: UserInput
  readonly showTaintedValues?: boolean
}

export function AttackEventRequest({
  request,
  userInput,
  verbose,
  showTaintedValues
}: AttackEventRequestProps & {
  readonly verbose?: boolean
}) {
  return verbose ? (
    <AttackEventRequestVerbose
      request={request}
      userInput={userInput}
      showTaintedValues={showTaintedValues}
    />
  ) : (
    <AttackEventRequestMinimal
      request={request}
      userInput={userInput}
      showTaintedValues={showTaintedValues}
    />
  )
}

function AttackEventRequestVerbose({
  request,
  userInput,
  showTaintedValues = true
}: AttackEventRequestProps) {
  const body = request.body
  return (
    <CodeSnippet>
      <div>
        <RequestSummary
          request={request}
          userInput={userInput}
          showTaintedValues={showTaintedValues}
        />
        {body && (
          <RequestBody
            request={{
              body
            }}
            userInput={userInput}
            showTaintedValues={showTaintedValues}
          />
        )}
      </div>
    </CodeSnippet>
  )
}

function AttackEventRequestMinimal({
  request,
  userInput,
  showTaintedValues = true
}: AttackEventRequestProps) {
  const body = request.body
  return (
    <CodeSnippet>
      <div
        className={css`
          display: block;
        `}
      >
        <RequestSummary
          request={request}
          userInput={userInput}
          showTaintedValues={showTaintedValues}
        />
        {body && (
          <RequestBody
            request={{ body }}
            userInput={userInput}
            showTaintedValues={showTaintedValues}
          />
        )}
      </div>
    </CodeSnippet>
  )
}

function RequestSummary({
  request,
  userInput,
  showTaintedValues
}: {
  readonly userInput: UserInput
  readonly showTaintedValues: boolean
  readonly request: Pick<
    HttpRequest,
    'method' | 'uri' | 'version' | 'queryString'
  >
}) {
  const { name, value, type } = userInput
  const { method, uri, version, queryString } = request
  const requestType = 'HTTP/'

  const hasTaintedMethod = type === 'METHOD'
  const hasTaintedUri = type === 'URI'

  return (
    <div>
      <span
        className={
          hasTaintedMethod && showTaintedValues ? taintedCss : undefined
        }
      >
        {method}{' '}
      </span>
      <span
        className={hasTaintedUri && showTaintedValues ? taintedCss : undefined}
      >
        {uri}
      </span>
      {typeof queryString !== 'undefined' && (
        <TaintedQueryString
          request={{ queryString }}
          userInput={{ name, value, type }}
          showTaintedValues={showTaintedValues}
        />
      )}
      <span> {requestType}</span>
      <span>{version}</span>
    </div>
  )
}

export function TaintedQueryString({
  request,
  userInput,
  showTaintedValues
}: {
  readonly request: {
    readonly queryString: HttpRequest['queryString']
  }
  readonly userInput: UserInput
  readonly showTaintedValues: boolean
}) {
  const { name, value, type } = userInput
  const { queryString } = request
  if (
    queryString === null ||
    typeof queryString === 'undefined' ||
    queryString.trim() === ''
  ) {
    return null
  }
  const hasTaintedQueryStringParameterName =
    type === 'PARAMETER_NAME' &&
    queryString.indexOf(name ? name.toLowerCase() : '') >= 0
  const hasTaintedQueryStringParameterValue =
    type === 'PARAMETER_VALUE' && queryString.indexOf(value.toLowerCase()) >= 0
  if (
    !hasTaintedQueryStringParameterName &&
    !hasTaintedQueryStringParameterValue
  ) {
    return (
      <>
        ?<WithRedactedTooltip text={queryString} />
      </>
    )
  }
  const taintedString = type === 'PARAMETER_NAME' ? name : value
  if (typeof taintedString !== 'undefined') {
    return (
      <>
        ?
        <TaintedString
          queryString={queryString}
          showTaintedValues={showTaintedValues}
          taintedString={taintedString}
        />
      </>
    )
  }
  return null
}

function RequestBody({
  userInput,
  request,
  showTaintedValues
}: {
  readonly userInput: UserInput
  readonly request: { readonly body: string }
  readonly showTaintedValues: boolean
}) {
  const { name, type, value } = userInput
  const { body } = request
  const bodyDecoded = decodeURIComponent(body)
    .split('+')
    .join(' ')
    .toLowerCase()

  const hasTaintedBodyParameterName =
    type === 'PARAMETER_NAME' &&
    bodyDecoded.indexOf((name || '').toLowerCase()) >= 0
  const hasTaintedBodyParameterValue =
    type === 'PARAMETER_VALUE' && bodyDecoded.indexOf(value.toLowerCase()) >= 0

  const taintedString = type === 'PARAMETER_NAME' ? name : value

  if (!hasTaintedBodyParameterName && !hasTaintedBodyParameterValue) {
    return (
      <div>
        <WithRedactedTooltip text={encodeURI(bodyDecoded)} />
      </div>
    )
  }
  if (typeof taintedString !== 'undefined') {
    return (
      <div>
        <TaintedString
          showTaintedValues={showTaintedValues}
          queryString={bodyDecoded}
          taintedString={taintedString}
        />
      </div>
    )
  }
  return null
}

export function TaintedString({
  queryString,
  taintedString,
  showTaintedValues
}: {
  readonly queryString: string
  readonly taintedString: string
  readonly showTaintedValues: boolean
}) {
  const startOfTaintedString = queryString.indexOf(taintedString)
  const endOfTaintedString = startOfTaintedString + taintedString.length
  if (startOfTaintedString < 0 || endOfTaintedString > queryString.length) {
    return <div />
  }
  const unTaintedStringStart = queryString.substring(0, startOfTaintedString)
  const unTaintedStringEnd = queryString.substring(
    endOfTaintedString,
    queryString.length
  )

  return (
    <React.Fragment>
      <WithRedactedTooltip text={encodeURI(unTaintedStringStart)} />
      <span className={showTaintedValues ? taintedCss : undefined}>
        <WithRedactedTooltip text={encodeURI(taintedString)} />
      </span>
      <WithRedactedTooltip text={encodeURI(unTaintedStringEnd)} />
    </React.Fragment>
  )
}
